import { Capacitor } from "@capacitor/core";
import { fetchGooglePlacePhotos, fetchNamedPlacePhoto, type GooglePlacePhotoChoice } from "../components/PlacePicker";
import {
  listTripEvents,
  listTripExperiences,
  listTripHotels,
  listTripItinerary,
  listTripPlaces,
  listTripPointsOfInterest,
  listTripRestaurants,
  type TripItineraryItem,
  type TripSummary
} from "./tripStore";

export type TripMediaCategory = "cities" | "hotels" | "restaurants" | "poi" | "experiences" | "events" | "itinerary";

export interface TripMediaPoint {
  id: string;
  sourceId: string;
  category: TripMediaCategory;
  name: string;
  city: string;
  address: string;
  latitude: number;
  longitude: number;
  date: string;
  endDate: string;
  time: string;
  googlePlaceId?: string;
  customPhotoDataUrl?: string;
  photoIndex?: number;
  photoEligible: boolean;
}

export interface UnresolvedItineraryPoint {
  id: string;
  sourceId: string;
  name: string;
  city: string;
  address: string;
  query: string;
  date: string;
  endDate: string;
  time: string;
}

export interface TripMediaPhoto {
  url: string;
  attributionName: string;
  attributionUrl?: string;
}

const googlePhotoCache = new Map<string, Promise<TripMediaPhoto | null>>();
const fallbackPhotoCache = new Map<string, Promise<TripMediaPhoto | null>>();

function normalized(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/[^a-z0-9à-ÿ]+/gi, " ").replace(/\s+/g, " ").trim();
}

function hasCoordinates(value: { latitude?: number; longitude?: number }): value is { latitude: number; longitude: number } {
  return typeof value.latitude === "number" && Number.isFinite(value.latitude) && typeof value.longitude === "number" && Number.isFinite(value.longitude);
}

export function tripDayNumber(trip: TripSummary, date: string): number | null {
  if (!trip.startDate || !date) return null;
  const start = Date.parse(`${trip.startDate}T00:00:00Z`);
  const current = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(current)) return null;
  const day = Math.floor((current - start) / 86400000) + 1;
  return day > 0 ? day : null;
}

export function tripDates(trip: TripSummary, maxDays = 120): string[] {
  if (!trip.startDate || !trip.endDate || trip.endDate < trip.startDate) return [];
  const start = Date.parse(`${trip.startDate}T00:00:00Z`);
  const end = Date.parse(`${trip.endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
  const output: string[] = [];
  for (let time = start; time <= end && output.length < maxDays; time += 86400000) {
    output.push(new Date(time).toISOString().slice(0, 10));
  }
  return output;
}

export function pointActiveOnDate(point: TripMediaPoint, date: string): boolean {
  if (!date) return true;
  if (!point.date) return false;
  const end = point.endDate || point.date;
  return point.date <= date && end >= date;
}

export function buildTripMediaPoints(uid: string, trip: TripSummary): TripMediaPoint[] {
  const points: TripMediaPoint[] = [];

  for (const item of listTripPlaces(uid, trip.id)) {
    if (!hasCoordinates(item)) continue;
    points.push({
      id: `city:${item.id}`,
      sourceId: item.id,
      category: "cities",
      name: item.name,
      city: item.city || item.name,
      address: item.address || item.city || item.name,
      latitude: item.latitude,
      longitude: item.longitude,
      date: item.startDate,
      endDate: item.endDate,
      time: "",
      googlePlaceId: item.googlePlaceId,
      customPhotoDataUrl: item.customPhotoDataUrl,
      photoIndex: item.photoIndex || 0,
      photoEligible: true
    });
  }

  for (const item of listTripHotels(uid, trip.id)) {
    if (!hasCoordinates(item)) continue;
    points.push({
      id: `hotel:${item.id}`,
      sourceId: item.id,
      category: "hotels",
      name: item.name,
      city: item.city,
      address: item.address || item.city || item.name,
      latitude: item.latitude,
      longitude: item.longitude,
      date: item.checkIn,
      endDate: item.checkOut,
      time: "",
      googlePlaceId: item.googlePlaceId,
      photoEligible: false
    });
  }

  for (const item of listTripRestaurants(uid, trip.id)) {
    if (!hasCoordinates(item)) continue;
    points.push({
      id: `restaurant:${item.id}`,
      sourceId: item.id,
      category: "restaurants",
      name: item.name,
      city: item.city,
      address: item.address || item.city || item.name,
      latitude: item.latitude,
      longitude: item.longitude,
      date: item.date,
      endDate: item.date,
      time: item.time,
      googlePlaceId: item.googlePlaceId,
      photoEligible: true
    });
  }

  for (const item of listTripPointsOfInterest(uid, trip.id)) {
    if (!hasCoordinates(item)) continue;
    points.push({
      id: `poi:${item.id}`,
      sourceId: item.id,
      category: "poi",
      name: item.name,
      city: item.city,
      address: item.address || item.city || item.name,
      latitude: item.latitude,
      longitude: item.longitude,
      date: item.visitDate,
      endDate: item.visitDate,
      time: "",
      googlePlaceId: item.googlePlaceId,
      photoEligible: true
    });
  }

  for (const item of listTripExperiences(uid, trip.id)) {
    if (!hasCoordinates(item)) continue;
    points.push({
      id: `experience:${item.id}`,
      sourceId: item.id,
      category: "experiences",
      name: item.title,
      city: item.city,
      address: item.address || item.placeName || item.city || item.title,
      latitude: item.latitude,
      longitude: item.longitude,
      date: item.date,
      endDate: item.endDate||item.date,
      time: item.time,
      googlePlaceId: item.googlePlaceId,
      photoEligible: true
    });
  }

  for (const item of listTripEvents(uid, trip.id)) {
    if (!hasCoordinates(item)) continue;
    points.push({
      id: `event:${item.id}`,
      sourceId: item.id,
      category: "events",
      name: item.title,
      city: item.city,
      address: item.address || item.venue || item.city || item.title,
      latitude: item.latitude,
      longitude: item.longitude,
      date: item.date,
      endDate: item.date,
      time: item.startTime,
      googlePlaceId: item.googlePlaceId,
      photoEligible: true
    });
  }

  return points.sort((a, b) => {
    if (a.date !== b.date) return (a.date || "9999-99-99").localeCompare(b.date || "9999-99-99");
    if (a.time !== b.time) return (a.time || "99:99").localeCompare(b.time || "99:99");
    return a.name.localeCompare(b.name);
  });
}

function bestExistingMatch(item: TripItineraryItem, points: TripMediaPoint[]): TripMediaPoint | undefined {
  const address = normalized(item.address);
  const city = normalized(item.city);
  const title = normalized(item.title);
  if (address) {
    const exactAddress = points.find((point) => normalized(point.address) === address);
    if (exactAddress) return exactAddress;
  }
  if (title) {
    const byName = points.find((point) => normalized(point.name) === title && (!city || normalized(point.city) === city));
    if (byName) return byName;
  }
  if (city) {
    const cityMatches = points.filter((point) => normalized(point.city) === city || normalized(point.name) === city);
    if (cityMatches.length === 1) return cityMatches[0];
  }
  return undefined;
}

export function buildMatchedItineraryPoints(uid: string, trip: TripSummary, contentPoints: TripMediaPoint[]): TripMediaPoint[] {
  return listTripItinerary(uid, trip.id).flatMap((item) => {
    const match = bestExistingMatch(item, contentPoints);
    if (!match) return [];
    return [{
      id: `itinerary:${item.id}`,
      sourceId: item.id,
      category: "itinerary" as const,
      name: item.title,
      city: item.city || match.city,
      address: item.address || match.address,
      latitude: match.latitude,
      longitude: match.longitude,
      date: item.date,
      endDate: item.date,
      time: item.startTime,
      googlePlaceId: match.googlePlaceId,
      photoEligible: false
    }];
  });
}

export function listUnresolvedItineraryPoints(uid: string, trip: TripSummary, contentPoints: TripMediaPoint[]): UnresolvedItineraryPoint[] {
  return listTripItinerary(uid, trip.id).flatMap((item) => {
    if (bestExistingMatch(item, contentPoints)) return [];
    const query = [item.address, item.city, item.title, trip.destination].map((value) => value.trim()).filter(Boolean).join(", ");
    if (!query) return [];
    return [{
      id: `itinerary:${item.id}`,
      sourceId: item.id,
      name: item.title,
      city: item.city,
      address: item.address || item.city || item.title,
      query,
      date: item.date,
      endDate: item.date,
      time: item.startTime
    }];
  });
}

async function loadWikimediaPhoto(point: TripMediaPoint, exactPlace = false): Promise<TripMediaPhoto | null> {
  const query = [point.name, point.city].map((value) => value.trim()).filter(Boolean).join(" ");
  if (!query) return null;
  const key = `${exactPlace}:` + query.toLocaleLowerCase();
  const cached = fallbackPhotoCache.get(key);
  if (cached) return cached;

  const request = (async () => {
    try {
      const params = new URLSearchParams({
        action: "query",
        generator: "search",
        gsrsearch: query,
        gsrnamespace: "6",
        gsrlimit: "6",
        prop: "imageinfo",
        iiprop: "url|extmetadata",
        iiurlwidth: "1000",
        format: "json",
        origin: "*"
      });
      const response = await fetch(`https://commons.wikimedia.org/w/api.php?${params.toString()}`, { headers: { Accept: "application/json" } });
      if (!response.ok) return null;
      const payload = await response.json() as { query?: { pages?: Record<string, { title?: string; imageinfo?: Array<{ thumburl?: string; url?: string; extmetadata?: Record<string, { value?: string }> }> }> } };
      const pages = Object.values(payload.query?.pages || {});
      const page = pages.find((entry) => {
        const info = entry.imageinfo?.[0];
        const url = String(info?.thumburl || info?.url || "");
        const tokens = normalized(point.name).split(" ").filter(word => word.length >= 4 && !["hotel","palace","mosque","madrasa","restaurant","cafe"].includes(word));
        const description = normalized([entry.title, info?.extmetadata?.ObjectName?.value, info?.extmetadata?.ImageDescription?.value].join(" "));
        const matchesName = tokens.length > 0 && tokens.every(token => description.includes(token));
        return /\.(?:jpe?g|png|webp)(?:$|\?)/i.test(url) && (!exactPlace || matchesName);
      });
      const info = page?.imageinfo?.[0];
      const url = String(info?.thumburl || info?.url || "").trim();
      if (!url) return null;
      const metadata = info?.extmetadata || {};
      const rawArtist = String(metadata.Artist?.value || metadata.Credit?.value || "Wikimedia Commons");
      const attributionName = rawArtist.replace(/<[^>]+>/g, " ").replace(/&nbsp;|&#183B3A;/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim() || "Wikimedia Commons";
      const attributionUrl = page?.title ? `https://commons.wikimedia.org/wiki/${encodeURIComponent(page.title.replace(/ /g, "_"))}` : "https://commons.wikimedia.org/";
      return { url, attributionName, attributionUrl } satisfies TripMediaPhoto;
    } catch {
      return null;
    }
  })();
  fallbackPhotoCache.set(key, request);
  return request;
}

export async function loadTripMediaPhoto(point: TripMediaPoint, exactPlace = false): Promise<TripMediaPhoto | null> {
  if (!point.photoEligible) return null;
  if (point.customPhotoDataUrl) return { url: point.customPhotoDataUrl, attributionName: "" };

  // Google Place photos are excellent on the web, but a WebView can be blocked by
  // browser-referrer restrictions. The mobile app therefore uses Commons as a
  // resilient fallback instead of leaving the map/album without photographs.
  if (point.googlePlaceId && !Capacitor.isNativePlatform()) {
    const index = Math.max(0, point.photoIndex || 0);
    const cacheKey = `${point.googlePlaceId}:${index}`;
    const existing = googlePhotoCache.get(cacheKey);
    const request = existing || fetchGooglePlacePhotos(point.googlePlaceId, Math.max(1, index + 1))
      .then((photos: GooglePlacePhotoChoice[]) => {
        const photo = photos[Math.min(index, Math.max(0, photos.length - 1))];
        return photo ? { url: photo.url, attributionName: photo.attributionName, attributionUrl: photo.attributionUrl } : null;
      })
      .catch(() => null);
    if (!existing) googlePhotoCache.set(cacheKey, request);
    const googlePhoto = await request;
    if (googlePhoto) return googlePhoto;
  }

  const commons = await loadWikimediaPhoto(point, exactPlace);
  if (commons || !exactPlace) return commons;
  try { return await fetchNamedPlacePhoto({name:point.name,city:point.city,address:point.address,latitude:point.latitude,longitude:point.longitude}); } catch { return null; }
}
